// Clasificación del retorno de Mercado Pago (única fuente de la decisión
// "¿hubo cobro o no?" para MercadoPagoReturnPage.jsx). Módulo puro: sin
// React, sin stores, para poder testear la regla de negocio de forma
// aislada y para que la página la ejercite tal cual (no se debe mockear en
// sus tests).
//
// Supuesto central de la regla "abandono" (regla 5 más abajo): un pago
// rechazado/creado real SIEMPRE trae `payment_id` — Mercado Pago crea el
// registro del intento apenas el usuario avanza en el checkout. Por lo
// tanto "sin payment_id y sin status/collection_status significativo" no
// puede ser un cobro: sólo puede darse cuando el usuario abandonó antes de
// intentar pagar (p. ej. "Ir a la tienda"/"Volver al sitio"). Este supuesto
// debe confirmarse empíricamente en QA (capturando la URL real de un
// abandono) antes de considerar cerrado el requerimiento — ver plan de
// implementación §7/P1.
//
// La regla es deliberadamente asimétrica: cualquier señal de posible cobro
// (payment_id válido, o un status/collection_status no vacío) manda siempre
// a "verificar" (el camino lento y seguro que ya existe). Sólo la ausencia
// total de señal se clasifica como "abandono".

export const MP_VERIFY_TIMEOUT_MS = 10000;

export const MP_MSG_ABANDONO = "Cancelaste el pago, tu carrito sigue disponible";
export const MP_MSG_RECHAZADO = "El pago fue rechazado por Mercado Pago";
export const MP_MSG_TIMEOUT_PREFIX =
  "No pudimos confirmar tu pago a tiempo. Revisá \"Mis compras\"; si el cobro se realizó, contactá a soporte con la referencia";

const STATUSES_VERIFICAR = [
  "approved",
  "authorized",
  "in_process",
  "pending",
  "in_mediation",
];

/**
 * sanitizeParam(v)
 * Devuelve null si el valor está ausente o es alguno de los literales que
 * Mercado Pago suele agregar como string en la URL de retorno cuando el
 * dato real no existe ("null", "undefined", "NaN"). Preserva cualquier otro
 * valor tal cual (incluido "0", que no debe confundirse con "ausente").
 */
export const sanitizeParam = (v) => {
  if (v === null || v === undefined) return null;
  const trimmed = String(v).trim();
  if (trimmed === "") return null;
  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "undefined" || lower === "nan") return null;
  return trimmed;
};

/**
 * classifyMpReturn(searchParams)
 * searchParams: objeto con `.get(name)` (p. ej. URLSearchParams).
 * Devuelve { kind, status, paymentId, externalReference } donde
 * kind ∈ "verificar" | "rechazado" | "abandono".
 */
export const classifyMpReturn = (searchParams) => {
  const paymentId =
    sanitizeParam(searchParams.get("payment_id")) ||
    sanitizeParam(searchParams.get("collection_id"));
  const status =
    sanitizeParam(searchParams.get("status")) ||
    sanitizeParam(searchParams.get("collection_status"));
  const result = sanitizeParam(searchParams.get("result"));
  const externalReference = sanitizeParam(searchParams.get("external_reference"));

  // Regla 1: cualquier payment_id (o collection_id) válido -> verificar.
  if (paymentId) {
    return { kind: "verificar", status, paymentId, externalReference };
  }

  // Regla 2: status/collection_status significativo -> verificar.
  if (status && STATUSES_VERIFICAR.includes(status.toLowerCase())) {
    return { kind: "verificar", status, paymentId, externalReference };
  }

  // Regla 3: forma legacy del salto por mp-return.vue.
  if (result === "success") {
    return { kind: "verificar", status, paymentId, externalReference };
  }

  // Regla 4: rechazo explícito (con status, sin payment_id — caso borde de
  // integración, no rompe la regla 1 porque ésta ya se evaluó antes).
  if (status && status.toLowerCase() === "rejected") {
    return { kind: "rechazado", status, paymentId, externalReference };
  }

  // Regla 5: sin payment_id y sin status/collection_status significativo ->
  // abandono. Cubre status ausente/"null", "cancelled", "failure",
  // result=pending y result=failure sin payment_id.
  return { kind: "abandono", status, paymentId, externalReference };
};
